// theme/muiTheme.ts
import { createTheme } from '@mui/material/styles';

const HEIGHTS = {
    small: 32,
    medium: 36,
    large: 40
};

export const muiTheme = createTheme({
    typography: {
        htmlFontSize: 16,
        fontSize: 14,
        // 👉 Đổi Roboto thành Inter
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        body1: { fontSize: 14 },
        body2: { fontSize: 13 },
        h6: { fontSize: 19, fontWeight: 650 }
    },
    palette: {
        primary: {
            main: '#087bbd'
        },
        secondary: {
            main: '#087bbd'
        },
        text: { primary: '#18354b', secondary: '#6b8193' },
        background: { default: '#f4f7fb', paper: '#ffffff' },
        divider: '#e9efef'
    },
    components: {
        // Đảm bảo body cũng dùng Inter theo theme
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif'
                }
            }
        },

        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                sx: {
                    minHeight: HEIGHTS.medium
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    minHeight: HEIGHTS.medium,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#93b4f5'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#087bbd',
                        borderWidth: 2
                    },
                    '&.MuiInputBase-multiline': {
                        height: 'auto'
                    },
                    '& .MuiChip-root': {
                        height: HEIGHTS.medium - 6,
                        fontSize: '0.75rem',
                        borderRadius: 6,
                        margin: 2
                    }
                },
                input: {
                    padding: '4px 12px',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.5
                },
                notchedOutline: {
                    borderColor: '#d0d7de'
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#4b5563'
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem'
                }
            }
        },
        MuiButton: {
            defaultProps: {
                size: 'medium',
                variant: 'contained',
                disableElevation: true
            },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    minHeight: HEIGHTS.medium,
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'background-color 150ms ease, border-color 150ms ease'
                },
                sizeSmall: { minHeight: 32, fontSize: 12 },
                sizeLarge: { minHeight: 44 }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    minHeight: HEIGHTS.medium,
                    '.MuiOutlinedInput-root': {
                        paddingRight: '34px !important'
                    }
                },
                inputRoot: {
                    padding: '0 !important',
                    minHeight: HEIGHTS.medium,
                    alignItems: 'center',
                    '& .MuiAutocomplete-input': {
                        padding: '0px 12px',
                        lineHeight: 1.5
                    }
                },
                tag: {
                    height: HEIGHTS.small,
                    fontSize: '0.75rem',
                    borderRadius: 6,
                    margin: 2,
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                },
                popupIndicator: {
                    top: 'calc(50% - 14px)'
                },
                clearIndicator: {
                    top: 'calc(50% - 14px)'
                }
            }
        },
        MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none', border: '1px solid #e9efef' }, rounded: { borderRadius: 8 } } },
        MuiMenu: { styleOverrides: { paper: { border: '1px solid #e9efef', borderRadius: 8, boxShadow: '0 12px 36px rgba(25,45,75,.12)' }, list: { padding: 6 } } },
        MuiMenuItem: { styleOverrides: { root: { borderRadius: 7, fontSize: 13, minHeight: 36, '&.Mui-selected': { backgroundColor: '#f2f8e6' } } } },
        MuiDialog: { styleOverrides: { paper: { borderRadius: 8, boxShadow: '0 24px 80px rgba(20,40,70,.2)' } } },
        MuiDialogTitle: { styleOverrides: { root: { padding: '20px 24px', fontSize: 19, fontWeight: 650, borderBottom: '1px solid #e9efef' } } },
        MuiDialogContent: { styleOverrides: { root: { padding: '20px 24px' } } },
        MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 6, borderTop: '1px solid #e9efef' } } },
        MuiTableCell: { styleOverrides: { root: { padding: '11px 14px', fontSize: 13, borderBottom: '1px solid #e9efef' }, head: { backgroundColor: '#f4f4f5', color: '#52525b', fontSize: 12, fontWeight: 650 } } },
        MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: '#18354b', fontSize: 12, padding: '7px 10px', borderRadius: 7 } } },
        MuiTabs: { styleOverrides: { root: { minHeight: 40 }, indicator: { height: 3, borderRadius: 3 } } },
        MuiTab: { styleOverrides: { root: { minHeight: 40, textTransform: 'none', fontSize: 13, fontWeight: 600 } } },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontSize: '0.75rem',
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                },
                label: {
                    paddingLeft: 6,
                    paddingRight: 6
                }
            }
        }
    }
});
